import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, RefreshCw, Tag, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button, Card, ErrorState, Input, SkeletonRows } from '../../../components/ui';
import { useFormat, useT } from '../../../i18n';
import { useAutomationCan, usePoolAccount, useRecheckAccount } from '../hooks';
import { AccountChanges } from './AccountChanges';
import { useDeletePoolAccount, useUpdatePoolAccount } from './api';
import { CheckResult } from './CheckResult';
import { Fail, Notice, useConfirm, useReadableError, useTestRunErrorText } from './shared';

/**
 * One pool account: the last check result, its label, a re-check and removal
 * from the pool, and its change history. A re-check logs in on the stand like
 * TestRun's did (with the 1 ₸ QR probe of the TSP contract).
 */

const BackLink = () => {
  const { t } = useT();
  return (
    <Link
      to="/automation/accounts"
      className="inline-flex items-center gap-1 self-start text-[13px] text-ink-muted hover:text-ink"
    >
      <ArrowLeft className="size-3.5" aria-hidden="true" />
      {t('automation.accounts.detail.back')}
    </Link>
  );
};

export function AccountDetailPage({ accountId }: { accountId: string }) {
  const { t } = useT();
  const { relative } = useFormat();
  const can = useAutomationCan();
  const navigate = useNavigate();
  const readable = useReadableError();
  const errorText = useTestRunErrorText();
  const confirm = useConfirm();
  const account = usePoolAccount(accountId);
  const save = useUpdatePoolAccount(accountId);
  const recheck = useRecheckAccount();
  const remove = useDeletePoolAccount(accountId);
  const [label, setLabel] = useState<string | null>(null);

  if (account.error) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink />
        <Card>
          <ErrorState error={readable(account.error)} onRetry={() => void account.refetch()} />
        </Card>
      </div>
    );
  }
  if (!account.data) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink />
        <SkeletonRows rows={5} className="p-0" />
      </div>
    );
  }

  const acc = account.data;
  const editing = label !== null;
  const doRecheck = () => recheck.mutate({ id: accountId });
  const saveLabel = (event: FormEvent) => {
    event.preventDefault();
    if (label === null) return;
    save.mutate(
      { label },
      {
        onSuccess: () => setLabel(null),
        onError: (error) => toast.error(errorText(error)),
      },
    );
  };
  const askRemove = () =>
    confirm.ask({
      title: t('automation.accounts.detail.removeConfirm'),
      description: t('automation.accounts.detail.removeHint'),
      confirmLabel: t('automation.accounts.detail.remove'),
      destructive: true,
      onConfirm: () =>
        remove.mutate(undefined, {
          onSuccess: () => {
            toast.success(t('automation.accounts.detail.removed'));
            void navigate({ to: '/automation/accounts' });
          },
          onError: (error) => toast.error(errorText(error)),
        }),
    });

  return (
    <div className="flex flex-col gap-4">
      <BackLink />

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
          <div className="min-w-72 space-y-1.5">
            {editing ? (
              <form onSubmit={saveLabel} className="flex flex-wrap items-center gap-2">
                <Input
                  autoFocus
                  aria-label={t('automation.accounts.detail.label')}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder={t('automation.accounts.detail.labelPlaceholder')}
                  className="w-80"
                />
                <Button type="submit" size="sm" variant="primary" loading={save.isPending}>
                  {t('common.save')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setLabel(null)}>
                  {t('common.cancel')}
                </Button>
              </form>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-[18px] font-semibold tracking-tight text-ink">
                  {acc.label || t('automation.accounts.detail.noLabel')}
                </h1>
                {can.run ? (
                  <Button size="sm" variant="ghost" onClick={() => setLabel(acc.label)}>
                    <Tag className="size-3.5" aria-hidden="true" />
                    {acc.label
                      ? t('automation.accounts.detail.editLabel')
                      : t('automation.accounts.detail.setLabel')}
                  </Button>
                ) : null}
              </div>
            )}
            <p className="font-mono text-[12px] text-ink-subtle">
              {acc.phone} · {t('automation.accounts.iin')} {acc.iin}
              {acc.bin ? ` · ${t('automation.accounts.bin')} ${acc.bin}` : ''}
            </p>
            <p className="text-[12px] text-ink-subtle">
              {t('automation.accounts.detail.inPoolSince', {
                when: relative(acc.addedAt),
                by: acc.addedBy,
              })}
              {' · '}
              {t('automation.accounts.detail.checked', {
                when: acc.checkedAt ? relative(acc.checkedAt) : t('common.empty'),
              })}
              {acc.checkedBy ? ` (${acc.checkedBy})` : ''}
            </p>
          </div>
          {can.run ? (
            <div className="flex gap-2">
              <Button
                onClick={doRecheck}
                loading={recheck.isPending}
                title={t('automation.accounts.detail.recheckHint')}
              >
                {recheck.isPending ? null : <RefreshCw className="size-4" aria-hidden="true" />}
                {recheck.isPending
                  ? t('automation.accounts.detail.rechecking')
                  : t('automation.accounts.detail.recheck')}
              </Button>
              <Button
                variant="ghost"
                className="text-fail"
                onClick={askRemove}
                loading={remove.isPending}
              >
                {remove.isPending ? null : <Trash2 className="size-4" aria-hidden="true" />}
                {t('automation.accounts.detail.remove')}
              </Button>
            </div>
          ) : null}
        </div>
      </Card>

      {recheck.isError ? (
        <Fail title={t('automation.accounts.detail.lastFailed')} error={recheck.error} />
      ) : acc.lastError ? (
        <Notice tone="fail" title={t('automation.accounts.detail.lastFailed')}>
          {acc.lastError.message}
        </Notice>
      ) : null}

      {acc.result ? (
        <CheckResult
          data={acc.result}
          creds={{ phone: acc.phone, iin: acc.iin, ...(acc.bin ? { bin: acc.bin } : {}) }}
          canAct={can.run}
          onRefresh={doRecheck}
        />
      ) : (
        <Card>
          <p className="px-4 py-6 text-center text-[13px] text-ink-muted">
            {t('automation.accounts.detail.noCheck')}
          </p>
        </Card>
      )}

      <AccountChanges poolId={accountId} />
      {confirm.dialog}
    </div>
  );
}
